// btw-front/src/components/sections/CertBear.jsx
import React from "react";
import Container from "../ui/Container";
import { Fade } from "react-awesome-reveal";
import { Button } from "../ui/Button";
import { Trans, useTranslation } from "react-i18next";

export default function CertBear() {
  const { t } = useTranslation();
  const CERTBEAR_WEB = `${process.env.PUBLIC_URL}/images/certbear/certbear_web.png`;

  const points = t("certbear.section.points", { returnObjects: true }) || [];
  const stats = t("certbear.section.stats", { returnObjects: true }) || [];
  const features = t("certbear.section.features", { returnObjects: true }) || [];

  return (
    <section
      id="certbear"
      className="relative overflow-hidden btw-section"
      aria-label={t("certbear.section.ariaLabel")}
    >
      {/* background decorations */}
      <div className="pointer-events-none absolute inset-0 btw-grid-bg opacity-[0.35]" />
      <div className="pointer-events-none absolute -top-40 left-1/2 h-[560px] w-[560px] -translate-x-1/2 rounded-full bg-orange-500/10 blur-3xl" />
      <div className="pointer-events-none absolute top-24 -left-24 h-[420px] w-[420px] rounded-full bg-slate-900/5 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-40 -right-40 h-[520px] w-[520px] rounded-full bg-orange-500/10 blur-3xl" />
      {/* CertBear 주력 강조: 차분한 accent halo */}
      <div className="pointer-events-none absolute -top-24 right-1/4 h-[420px] w-[420px] rounded-full bg-accent/10 blur-3xl" />

      <Container className="relative">
        <div className="grid items-center gap-10 lg:grid-cols-2">
          {/* LEFT: copy */}
          <Fade direction="up" triggerOnce>
            <div>
              <span className="btw-eyebrow">Featured Solution</span>
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <span className="btw-chip">
                  <span className="inline-block h-2 w-2 rounded-full bg-orange-500" />
                  {t("certbear.section.chip1")}
                </span>
                <span className="btw-chip">{t("certbear.section.chip2")}</span>
              </div>

              <h2 className="mt-5 text-3xl font-semibold tracking-tight text-slate-900 md:text-4xl">
                {t("certbear.section.title1")}
                <br className="hidden sm:block" />
                {t("certbear.section.title2")}
              </h2>

              <p className="mt-5 whitespace-pre-line text-base leading-relaxed text-slate-600 md:text-lg">
                {t("certbear.section.p1")}
              </p>

              <p className="mt-4 text-base leading-relaxed text-slate-600 md:text-lg">
                <Trans
                  i18nKey="certbear.section.p2"
                  components={{ b: <b className="text-slate-900" /> }}
                />
              </p>

              {/* key bullets */}
              <div className="mt-7 grid gap-3 sm:grid-cols-2">
                {points.map((p) => (
                  <Point key={p.title} title={p.title} desc={p.desc} />
                ))}
              </div>

              {/* CTA */}
              <div className="mt-9 flex flex-wrap items-center gap-3">
                <Button variant="accent" href="#contact">
                  {t("certbear.section.cta1")}
                </Button>
                <Button variant="ghost" href="#services">
                  {t("certbear.section.cta2")}
                </Button>
              </div>

              {/* small stats */}
              <div className="mt-10 grid gap-3 sm:grid-cols-3">
                {stats.map((s) => (
                  <Stat key={s.k} k={s.k} v={s.v} />
                ))}
              </div>
            </div>
          </Fade>

          {/* RIGHT: image mockup */}
          <Fade direction="up" triggerOnce>
            <div className="relative">
              <div className="btw-card relative overflow-hidden p-3 md:p-4">
                <div className="absolute inset-0 bg-gradient-to-b from-white/70 to-white/40" />
                <div className="relative">
                  <img
                    className="w-full rounded-2xl border border-slate-200 bg-white object-cover shadow-sm"
                    src={CERTBEAR_WEB}
                    alt={t("certbear.section.imageAlt")}
                    loading="lazy"
                  />
                </div>
              </div>

              {/* floating badge */}
              <div className="absolute -bottom-6 -right-2 rotate-[6deg]">
                <div className="rounded-2xl border border-orange-200 bg-white/80 px-4 py-3 shadow-sm backdrop-blur">
                  <div className="text-xs font-medium text-orange-600">
                    {t("certbear.section.floating.label")}
                  </div>
                  <div className="mt-1 text-sm font-semibold text-slate-900">
                    {t("certbear.section.floating.title")}
                  </div>
                </div>
              </div>

              <div className="pointer-events-none absolute -top-6 -left-6 h-24 w-24 rounded-3xl bg-orange-500/10 blur-xl" />
            </div>
          </Fade>
        </div>

        {/* bottom: feature strip */}
        <Fade direction="up" triggerOnce>
          <div className="mt-12 grid gap-4 md:grid-cols-3">
            {features.map((f) => (
              <Feature key={f.title} title={f.title} desc={f.desc} />
            ))}
          </div>
        </Fade>
      </Container>
    </section>
  );
}

function Point({ title, desc }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white/70 p-4 backdrop-blur">
      <div className="text-sm font-semibold text-slate-900">{title}</div>
      <div className="mt-1 text-sm text-slate-600">{desc}</div>
    </div>
  );
}

function Stat({ k, v }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white/60 p-4 text-center backdrop-blur">
      <div className="text-xs text-slate-500">{k}</div>
      <div className="mt-1 text-sm font-semibold text-slate-900">{v}</div>
    </div>
  );
}

function Feature({ title, desc }) {
  const { t } = useTranslation();
  return (
    <div className="btw-card-hover p-6">
      <div className="text-lg font-semibold text-slate-900">{title}</div>
      <p className="mt-3 text-sm leading-relaxed text-slate-600">{desc}</p>
      <div className="mt-5 h-px w-full bg-slate-200" />
      <div className="mt-4 text-xs text-slate-500">{t("certbear.section.featureNote")}</div>
    </div>
  );
}
